import { useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import ModalService from "src/modals"
import { logoutUser } from "src/store/auth"
import { deleteAccount, selectUsername } from "src/store/user"

import "./DeleteAccount.css"

/* Closing the account, and the net under the button (owner's ask, 2026-09-09: "with the
   corresponding safety net for clicking accidentally").

   The net is **typing your own name**, not a yes/no dialog. A confirmation dialog is answered by
   the same reflex that pressed the button — one more click, in the same place, a quarter of a
   second later — and this is the one action in the app that nothing can undo. Copying six
   characters out of the sentence above the box takes a different kind of attention, and it is the
   attention this needs. The button stays disabled until the name matches exactly, so there is no
   moment where a stray Enter closes anything.

   It is also **shut by default**: the row is a link, and the field and the red button only exist
   after the player has asked for them. A permanently visible DELETE ACCOUNT under the language
   picker is a thing to mis-click.

   What actually happens is stated rather than implied — the games stay, the account stops being a
   login — because a player deciding this should not have to guess which of the two it is. */
const DeleteAccount = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const username = useSelector(selectUsername)

  const [opened, setOpened] = useState(false)
  const [typed, setTyped] = useState("")
  const [closing, setClosing] = useState(false)
  const [refusal, setRefusal] = useState(null)

  const matches = typed.trim() === (username ?? "")

  const close = async (event) => {
    event.preventDefault()
    if (!matches || closing) return

    setClosing(true)
    try {
      await dispatch(deleteAccount()).unwrap()
      // The session goes the way it goes on any sign-out: every slice reset, the stored tokens
      // cleared. Doing it here rather than in the thunk keeps the thunk about the request.
      dispatch(logoutUser())
      ModalService.hideAll()
    } catch (rejected) {
      setRefusal(rejected?.message ?? t("errors.somethingWentWrong", { defaultValue: "Something went wrong." }))
      setClosing(false)
    }
  }

  if (!opened) {
    return (
      <div className="delete-account">
        <button type="button" className="quiet delete-account-open" onClick={() => setOpened(true)}>
          {t("settings.deleteAccount")}
        </button>
      </div>
    )
  }

  return (
    <form className="delete-account opened" onSubmit={close}>
      <h3 className="delete-account-title">{t("settings.deleteAccount")}</h3>
      <p className="p-mini delete-account-warning">{t("settings.deleteAccountWarning")}</p>
      <p className="p-mini delete-account-confirm">{t("settings.deleteAccountConfirm", { username })}</p>
      <input
        type="text"
        className="form-input"
        value={typed}
        autoFocus
        autoComplete="off"
        aria-label={t("settings.deleteAccountConfirm", { username })}
        onChange={(event) => {
          setTyped(event.target.value)
          setRefusal(null)
        }}
        onKeyDown={(event) => event.key === "Escape" && setOpened(false)}
      />
      {refusal && (
        <p className="p-mini delete-account-refusal" role="alert">
          {refusal}
        </p>
      )}
      <div className="delete-account-actions">
        <button type="button" className="quiet" onClick={() => setOpened(false)}>
          {t("settings.deleteAccountKeep")}
        </button>
        <button type="submit" className="cancel" disabled={!matches || closing}>
          {t("settings.deleteAccountGo")}
        </button>
      </div>
    </form>
  )
}

export default DeleteAccount
